/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback} from 'react';
import {makeStyles} from '@material-ui/core';
import {translate} from '@ion/core/src/locale/locale';
import {GenericObject, SensorType} from '@ion/core/src/types/entities';
import {getAssetDetailsContentStyles} from '@ion/dls/src/components/assetdetails/current/assetDetailsContentStyles';
import {SCIDeviceSettingsUnits} from '@ion/dls/src/editors/DeviceEditorUtils';
import {SensorTypeIcon} from '@ion/dls/src/icons/SensorTypeIcon';
import {themedClassName} from '@ion/dls/src/theme/helpers';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {isCurrentTheme} from '@ion/dls/src/theme/useThemeContext';
import {DLSTheme} from '@ion/dls/src/types/types';
import {getFormattedDate} from '@ion/dls/src/utils/ComponentUtils';
import {getNumberDecimal} from '@ion/query/src/utils/helpers';
import DeviceStatusLegends from './DeviceStatusLegends';
import {getCurrentStatusTimeStyles} from './statusTimeStyles';
import {getSensorDataAttributes} from './utils';

export interface TiltSensorStatusProps {
  data: GenericObject;
  isOverThreshold?: boolean;
}

const useStyles = makeStyles((theme: DLSTheme) => {
  const {valueText, tile} = getAssetDetailsContentStyles();
  return {
    statusContainer : {
      display        : 'flex',
      alignItems     : 'center',
      justifyContent : 'space-between',
      padding        : '10px 15px 0'
    },
    statusIcon : {
      color                          : dlsColor('grey3'),
      textTransform                  : 'uppercase',
      letterSpacing                  : '1px',
      display                        : 'flex',
      alignItems                     : 'center',
      [theme.breakpoints.down('sm')] : {
        flexDirection : 'column',
        alignItems    : 'flex-start'
      }
    },
    status : {
      [theme.breakpoints.down('sm')] : {
        padding : '5px 0'
      }
    },
    statusText : {
      fontSize       : '20px',
      width          : '100%',
      display        : 'flex',
      flexDirection  : 'column',
      alignContent   : 'center',
      textAlign      : 'center',
      justifyContent : 'flex-end',
      color          : dlsColor('grey3')
    },
    sensorIconContainer : {
      display        : 'flex',
      flexDirection  : 'column',
      alignItems     : 'center',
      justifyContent : 'center',
      whiteSpace     : 'nowrap'
    },
    icon : {
      marginRight : '8px'
    },
    current : {
      fontFamily     : 'Roboto',
      fontSize       : '10px',
      fontWeight     : 700,
      marginTop      : '2px',
      letterSpacing  : '1px',
      textTransform  : 'uppercase',
      display        : 'flex',
      alignItems     : 'center',
      justifyContent : 'flex-end',
      padding        : '0 15px',
      color          : dlsColor('grey3')
    },
    containerTopRow : {
      display    : 'flex',
      alignItems : 'center'
    },
    // current theme renders the status row as the shared bordered tile and
    // maps the big value's typography to the shared `value` metrics - the
    // semantic normal/alert color stays the inline style it already is ...
    currentStatusContainer : {
      ...tile,
      padding : '8px'
    },
    currentStatusText : {
      ...valueText
    },
    currentTimeText : getCurrentStatusTimeStyles()
  };
});

const TiltSensorStatus = ({data, isOverThreshold}: TiltSensorStatusProps) => {
  const classes        = useStyles();
  const isCurrent      = isCurrentTheme();
  const normal         = translate('tracking:deviceStatus.normal');
  const alert          = translate('tracking:deviceStatus.alert');
  const degree         = SCIDeviceSettingsUnits.Degree;
  const normalIcon     = SensorType.Tilt;
  const tiltedIcon     = SensorType.Tilted;
  const {y, eventTime} = getSensorDataAttributes(data);
  const currentValue   = getNumberDecimal(y, 0);
  const currentTime    = getFormattedDate(eventTime);

  const getCurrentColor = useCallback((isOverThreshold: boolean) => {
    if (isOverThreshold) {
      return dlsColor('caRed');
    }
    return dlsColor('caBlue');
  }, [isOverThreshold]);

  return (
    <>
      <div className={themedClassName(classes.statusContainer, classes.currentStatusContainer, isCurrent)}>
        <DeviceStatusLegends normalText={normal} thresholdText={alert} />
        <div className={classes.sensorIconContainer}>
          <div className={classes.containerTopRow}>
            <span className={classes.icon}>
              {isOverThreshold ? (
                <SensorTypeIcon sensorType={tiltedIcon} color={dlsColor('caRed')} size={25} />
              ) : (
                <SensorTypeIcon sensorType={normalIcon} />
              )}
            </span>
            <div className={themedClassName(classes.statusText, classes.currentStatusText, isCurrent)}>
              <span style={{color: `${getCurrentColor(isOverThreshold)}`}}>
                {`${currentValue}${degree}`}
              </span>
            </div>
          </div>
        </div>
      </div>
      <span className={themedClassName(classes.current, classes.currentTimeText, isCurrent)}
        style={{color: `${getCurrentColor(isOverThreshold)}`}}>
        {currentTime}
      </span>
    </>
  );
};

export default TiltSensorStatus;
